import http from "~/api/http";
import {
    Chain,
    CreateOrder,
    Token,
    TokenBase,
    TokenCandle,
    TokenWithBalance,
    TokenWithdrawHistory,
    TradeOrder,
    TransformedToken,
    UserWithdrawlBalance,
} from "~/types";
import qs from "qs";
import { tgLink, tgLinkType } from "~/utils";
import {
    UserCommission,
    UserGroupRecord,
    UserInviteRecord,
    UserRebateRecord,
} from "~/types/api/dex/rebate";
import { SuggestionAmountsMap } from "~/pages/Trade/hooks/useSuggectionAmounts";
import { entries, first } from "lodash-es";
import { isBuiltInToken } from "~/pages/Trade/utils";

export const fetchChainList = () =>
    http.get<{ chains: Chain[] }>("/quote/chain/list");

export const fetchTokenRankingList = (
    chain_id: string,
    ranking_type: number,
    duration_type: number
) => {
    const params = {
        chain_id,
        ranking_type,
        duration_type,
        size: 100,
    };
    return http.get<{ list: Token[] }>(
        `/quote/chain/ranking?${qs.stringify(params)}`
    );
};

export const searchTokenByKeyword = (keyword: string) => {
    return http.get<{ tokens: Token[] }>(
        `/quote/token/get_list?keyword=${keyword}`
    );
};

export const fetchTokenMarket = (chain_id: string, contract: string) => {
    return http.get<
        Token & { token_risk: null | { risk_level?: 0 | 1 | 2 | 3 } }
    >(`/quote/token/market?chain_id=${chain_id}&contract=${contract}`);
};

export const fetchTokenDetail = (chain_id: string, contract: string) => {
    return http.get(
        `/quote/token/information?chain_id=${chain_id}&contract=${contract}`
    );
};

export const fetchTokenTradePair = (params: {
    chain_id: string;
    contract: string;
    address: string;
    address_type: number;
}) => {
    return http.get<{ from: TokenWithBalance; to: TokenWithBalance }>(
        `/token/trade_pair?${qs.stringify(params)}`
    );
};

export const fetchTokenTXS = (
    chain_id: string,
    contract: string,
    type: string
) => {
    return http.get<{
        market_cap: string;
        list: Array<{ type: number; amount: string; value: string }>;
    }>(`/token/txs?chain_id=${chain_id}&contract=${contract}&type=${type}`);
};

export const fetchOrderStables = (
    chain_id: string,
    address: string,
    address_type: number
) => {
    const params = {
        chain_id,
        address,
        address_type,
    };
    return http.get<{ list: TokenWithBalance[] }>(
        `/order/stables?${qs.stringify(params)}`
    );
};

export const fetchApproveCheck = (
    chain_id: string,
    address: string,
    address_type: number,
    contract: string
) => {
    return http.post(`/order/approve/check`, {
        address: address,
        address_type: address_type,
        chain_id: chain_id,
        contract: contract,
    });
};

export const fetchApproveResult = (chain_id: string, hash: string) => {
    // 1 代表链上未查询到；2 代表链上 pending 中；3 代表链上失败；4 代表链上成功
    return http.get<{ status: number }>(
        `/tx/check?chain_id=${chain_id}&hash=${hash}`
    );
};

export const fetchOrderQuota = (params: {
    from_chain_id: number;
    from_contract: string;
    from_amount: string;
    to_chain_id: number;
    to_contract: string;
    to_amount: string;
    slippage: number;
}) => {
    return http.post(`/order/quote`, params);
};

export const createOrder = (params: {
    from_address: string;
    from_chain_id: number;
    from_contract: string;
    from_amount: string;
    to_address: string;
    to_chain_id: number;
    to_contract: string;
    to_amount: string;
    slippage: number;
    is_auto_slippage: boolean;
    protocol?: string;
    order_type: number;
    direction: number;
}) => {
    const groupId = window.sessionStorage.getItem(
        "__uxuy_dex_cached_referral_group_id__"
    );

    return http.post<CreateOrder>(`/order/create`, params, {
        headers: groupId ? { "x-referral-group-id": groupId } : {},
    });
};

export const submitOrder = (
    order_id: string,
    hash: string | null | undefined,
    signed_data: string | undefined
) => {
    return http.post(`/order/submit`, {
        order_id,
        hash,
        signed_data,
    });
};

export const fetchOrderDetail = (order_id: string) => {
    return http.get<{ detail: TradeOrder }>(
        `/order/detail?order_id=${order_id}`
    );
};

export const fetchOrderList = (args: {
    direction?: string;
    chain_id?: string;
    contract?: string;
}) => {
    const params: { offset: number; size: number; direction?: string } = {
        offset: 0,
        size: 200,
        ...args,
    };
    return http.get<{ list: TradeOrder[] }>(
        `/order/list?${qs.stringify(params)}`
    );
};

export const fetchWalletBalance = (
    addresses: Array<{ address: string; address_type: number }>
) => {
    return http.post<{
        value: string;
        change: string;
        list: TokenWithBalance[];
    }>(`/account/wallet`, {
        addresses: addresses,
    });
};

export const fetchTokenBalances = (
    address: string,
    address_type: number,
    tokens: Array<{ chain_id: string; contract: string }>
) => {
    return http.post<{ list: Array<TokenWithBalance> }>(
        `/account/token/balances`,
        {
            addresses: [
                {
                    address_type,
                    address,
                },
            ],
            tokens,
        }
    );
};

export const fetchTokenBalance = (params: {
    chain_id: string;
    contract: string;
    address: string;
    address_type: number;
}) => {
    return http.get(`/account/token/balance?${qs.stringify(params)}`);
};

export const connectWallet = (
    addresses: Array<{ address_type: number; address: string }>
) => {
    return http.post("/wallet_connect", {
        addresses: addresses.map((item) => ({
            type: item.address_type,
            address: item.address,
        })),
    });
};

export const fetchTokenCommission = (chain_id: string, contract: string) => {
    return http.get<{
        amount: number;
        ratio: number;
        symbol: string;
        value: string;
    }>(`/account/token/commission?chain_id=${chain_id}&contract=${contract}`);
};

export const fetchUserRebate = () => {
    return http.get<UserCommission>("/commission/info?offset=0&size=100");
};

export const fetchCommissionDetail = (chain_id: string, contract: string) => {
    return http.get(
        `/commission/detail?chain_id=${chain_id}&contract=${contract}&offset=0&size=100`
    );
};

export const fetchTokenChanges = (chain_id: string, contract: string) => {
    return http.get(
        `/quote/token/change?chain_id=${chain_id}&contract=${contract}`
    );
};

export const fetchTokenCandles = (
    chain_id: string,
    contract: string,
    bar_type: string,
    ts: number,
    size: number
) => {
    return http.get<{
        err_msg: string;
        start_ts: number;
        end_ts: number;
        result: TokenCandle[];
    }>(
        `/quote/token/candles?chain_id=${chain_id}&contract=${contract}&bar_type=${bar_type}&size=${size}&ts=${ts}&after=0`
    );
};

export const fetchSuggestionTokens = () => {
    return http.get<{ list: TokenBase[] }>(`/favorite/recommend/tokens`);
};

export const fetchFavoriteTokens = (
    rankingType: number,
    durationType: number
) => {
    return http.get<{ list: Token[] }>(
        `/favorite/tokens?ranking_type=${rankingType}&duration_type=${durationType}`
    );
};

export const postAddFavoriteToken = (chain_id: string, contract: string) => {
    return http.post("/favorite/add", {
        chain_id,
        contract,
    });
};

export const postBatchAddFavoriteToken = (
    tokens: Array<{ chain_id: string; contract: string }>
) => {
    return http.post("/favorite/batch_add", { tokens });
};

export const postRemoveFavoriteToken = (chain_id: string, contract: string) => {
    return http.post("/favorite/remove", {
        chain_id,
        contract,
    });
};

export const fetchTrendingConfig = () => {
    return http.get(`/trending/config`);
};

export const fetchTrendingList = (ranking_type?: number) => {
    return http.get<{ list: Token[] }>(
        `/trending/list?range_type=${ranking_type}`
    );
};

export const fetchTokenSearchHotList = () => {
    return http.get<{ list: Token[] }>("/token/search/hot");
};

export const fetchSpotlightTokens = () => {
    return http.get<{
        list: Array<{ token_info: Token; candles: TokenCandle[] }>;
    }>("/spotlight/tokens");
};

export const fetchTrasformedToken = (text: string) => {
    return http.post<TransformedToken>("/link/transform", { text });
};

export const uploadImage = (name: string, imgBase64: string) => {
    return http.post<{ url: string }>("/image/upload", {
        name: name,
        data: imgBase64,
    });
};

function objectToBase64(obj: object) {
    // 将对象转换为 JSON 字符串
    const jsonString = JSON.stringify(obj);

    // 将字符串转换为 Uint8Array
    const encoder = new TextEncoder();
    const uint8Array = encoder.encode(jsonString);

    // 将 Uint8Array 转换为字符串形式
    const binaryString = Array.from(uint8Array)
        .map((byte) => String.fromCharCode(byte))
        .join("");

    // 使用 btoa 进行 Base64 编码
    return btoa(binaryString);
}

export const savePhotoMessage = (
    userId: number,
    imgUrl: string,
    text: string,
    contract: string,
    chain_id: string,
    invite_code: string,
    t: (key: string) => string
) => {
    const id = (new Date().getTime() + Math.random()).toString(16);
    const width = 375;
    const height = 240;
    const pixelRatio = 2; // window.devicePixelRatio || 1;
    const buildInlineQueryResult = () => {
        return objectToBase64({
            type: "photo",
            id,
            photo_url: imgUrl,
            thumbnail_url: imgUrl,
            photo_width: width * pixelRatio,
            photo_height: height * pixelRatio,
            caption: text,
            reply_markup: {
                inline_keyboard: [
                    [
                        {
                            text: t("share_2_earn_kline_btn"),
                            url: tgLink._getLink(tgLinkType.B, {
                                contract: contract,
                                chain_id: chain_id,
                                invite_user_code: invite_code,
                                params: {},
                            }) as string,
                        },
                        {
                            text: t("share_2_earn_trade_btn"),
                            url: tgLink._getLink(tgLinkType.P, {
                                pathname: "trade",
                                contract: contract,
                                chain_id: chain_id,
                                invite_user_code: invite_code,
                                params: {},
                            }) as string,
                        },
                    ],
                ],
            },
        });
    };

    return http.post<{ id: string }>("/tgbot/save_prepared_inline_message", {
        uid: userId,
        inline_query_result: buildInlineQueryResult(),
        allow_user_chats: true,
        allow_bot_chats: true,
        allow_group_chats: true,
        allow_channel_chats: true,
    });
};

export const saveArticleMessage = (
    userId: number,
    url: string,
    text: string,
    contract: string,
    chain_id: string,
    invite_code: string,
    t: (key: string) => string
) => {
    const id = (new Date().getTime() + Math.random()).toString(16);
    const buildInlineQueryResult = () => {
        const firstLine = text?.split?.("\n")?.[0] ?? "";

        let quickBuyItems: any[] = [];
        if (!isBuiltInToken(contract)) {
            const tokens = SuggestionAmountsMap[String(chain_id)];
            if (tokens) {
                const firstMap = first(entries(tokens));
                if (firstMap) {
                    const [symbol, amounts] = firstMap;
                    quickBuyItems = amounts.map((item) => ({
                        text: `${item} ${symbol}`,
                        url: tgLink._getLink(tgLinkType.Q, {
                            contract: contract,
                            chain_id: chain_id,
                            from_amount: String(item),
                            invite_user_code: invite_code,
                            params: {},
                        }) as string,
                    }));
                }
            }
        }

        return objectToBase64({
            type: "article",
            id,
            title: firstLine,
            input_message_content_2: {
                message_text: text,
                link_preview_options: {
                    is_disabled: false,
                    url: url,
                    show_above_text: true,
                },
            },
            url: url,
            hide_url: true,
            reply_markup: {
                inline_keyboard: [
                    [
                        {
                            text: t("share_2_earn_kline_btn"),
                            url: tgLink._getLink(tgLinkType.B, {
                                contract: contract,
                                chain_id: chain_id,
                                invite_user_code: invite_code,
                                params: {},
                            }) as string,
                        },
                        {
                            text: t("share_2_earn_trade_btn"),
                            url: tgLink._getLink(tgLinkType.P, {
                                pathname: "trade",
                                contract: contract,
                                chain_id: chain_id,
                                invite_user_code: invite_code,
                                params: {},
                            }) as string,
                        },
                    ],
                    quickBuyItems,
                ],
            },
        });
    };

    return http.post<{ id: string }>("/tgbot/save_prepared_inline_message", {
        uid: userId,
        inline_query_result: buildInlineQueryResult(),
        allow_user_chats: true,
        allow_bot_chats: true,
        allow_group_chats: true,
        allow_channel_chats: true,
    });
};

export const fetchRebateRecordList = (page_num: number, page_size: number) => {
    return http
        .get<UserRebateRecord>(
            `/rebate/record?page=${page_num}&size=${page_size}`
        )
        .then((res) => res.data.list);
};

export const fetchInviteRecordList = (
    page_num: number,
    page_size: number,
    sort_type: number
) => {
    return http
        .get<UserInviteRecord>(
            `/invite/record?page=${page_num}&size=${page_size}&sort_type=${sort_type}`
        )
        .then((res) => res.data.list);
};

export const fetchGroupRecordList = () => {
    return http
        .get<UserGroupRecord>(`/bot_group/rebates`)
        .then((res) => res.data.list);
};

export const fetchWithdrawnBalance = () => {
    return http
        .get<UserWithdrawlBalance>(`/withdrawal/balance`)
        .then((res) => res.data);
};

export const createWithdrawnRequest = () => {
    return http.post(`/withdrawal/create`, {});
};

export const fetchWithdrawnHistory = () => {
    return http
        .get<{ list: TokenWithdrawHistory[] }>(`/withdrawal/history`)
        .then((res) => res.data);
};
